import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { Button, ImageIcon } from '@saleshandy/design-system';
import ReactSelect, { components } from 'react-select';
import { debounce } from 'lodash';
import Select from '../../../../../../../../../../shared/design-system/components/select';
import {
  customStyles,
  COUNTRY_ISO_FLAG_MAP,
  PHONE_TYPE_OPTIONS,
} from '../../../../constants';
import { searchRegions } from '../../../../../../../../helpers/dialer.api';
import { CountryOptionForNumbersSearch } from '../../../../../../../../types';
import { SearchPhoneNumbersPayload } from '../../../../../../../../types/phone-numbers';
import { accessibleOnClick } from '../../../../../../../../../../shared/utils/accessible-on-click';
import { RequestStatus } from '../../../../../../../../../../shared/enums/request-status';
import { getIsRequestPending } from '../../../../../../../../../../shared/utils/get-request-status';
import { executeOnRequestStatus } from '../../../../../../../../../../shared/utils/execute-on-request-status';
import { RequestState } from '../../../../../../../../../../shared/types/request-state';

interface PhoneTypeOption {
  key: string;
  value: string;
  isDisabled?: boolean;
}
interface LocalityApiResponse {
  id: number;
  region: string;
  locality: string;
  regionCode: string;
}
interface AsyncRegionOption {
  value: {
    locality: string;
    region: string;
    regionCode: string;
  };
  label: string;
  key: string;
}

interface SearchFilters {
  selectedCountry: {
    isoCode: string;
    id: number;
    isMobileNumberAvailable: boolean;
    isLocalNumberAvailable: boolean;
    isLocalityRequired: boolean;
  } | null;
  // selectedPhoneType: string | null;
  selectedRegion: {
    locality: string;
    region: string;
    regionCode: string;
  } | null;
}

const REGION_PAGE_SIZE = 20;

// Defined at module level so ReactSelect never sees a new reference between renders.
// Data flows in via selectProps (standard react-select pattern for custom component props).
const RegionMenuList = (props: any) => {
  const { onScrollNearBottom, isLoadingMoreRegions } = props.selectProps;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      onScrollNearBottom();
    }
  };

  return (
    <components.MenuList
      {...props}
      innerProps={{ ...props.innerProps, onScroll: handleScroll }}
    >
      {props.children}
      {isLoadingMoreRegions && (
        <div className="region-loading-more">Loading...</div>
      )}
    </components.MenuList>
  );
};

export interface SearchNumbersProps {
  countriesOptionsForNumbersSearch: CountryOptionForNumbersSearch[];
  getCountriesOptionsForNumbersSearchRequest: () => void;
  searchPhoneNumbersRequest: (payload: SearchPhoneNumbersPayload) => void;
  searchPhoneNumbersRequestStatus: RequestStatus;
  setIsRegionChangedInLastSearch: (value: boolean) => void;
  setLastSearchRegion: (region: string | null) => void;
  resetPurchasePhoneNumbersCartRequest: () => void;
  purchasePhoneNumbersCartRequestState: RequestState;
  getPhoneNumbersCartRequest: () => void;
}

const SearchNumbers: React.FC<SearchNumbersProps> = ({
  countriesOptionsForNumbersSearch,
  getCountriesOptionsForNumbersSearchRequest,
  searchPhoneNumbersRequest,
  searchPhoneNumbersRequestStatus,
  setIsRegionChangedInLastSearch,
  setLastSearchRegion,
  resetPurchasePhoneNumbersCartRequest,
  purchasePhoneNumbersCartRequestState,
  getPhoneNumbersCartRequest,
}) => {
  const isSearching = getIsRequestPending(searchPhoneNumbersRequestStatus);

  const [searchFilters, setSearchFilters] = useState<SearchFilters | null>(
    null,
  );

  const [
    selectedRegionOption,
    setSelectedRegionOption,
  ] = useState<AsyncRegionOption | null>(null);
  const [regionInputValue, setRegionInputValue] = useState('');
  const [isRegionFocused, setIsRegionFocused] = useState(false);
  const [regionOptions, setRegionOptions] = useState<AsyncRegionOption[]>([]);
  const [isRegionLoading, setIsRegionLoading] = useState(false);

  // Cache initial (empty query) options so clearing input restores them instantly
  const initialRegionOptionsRef = useRef<AsyncRegionOption[]>([]);
  const hasLoadedInitialRef = useRef(false);

  // Debounce ref for typed searches
  const debouncedFetchOptionsRef = useRef<ReturnType<typeof debounce>>();

  // Track previous region to detect changes
  const previousRegionRef = useRef<string | null>(null);

  // Server-side pagination state
  const [regionCurrentPage, setRegionCurrentPage] = useState(1);
  const [regionTotalPages, setRegionTotalPages] = useState(1);
  const [isLoadingMoreRegions, setIsLoadingMoreRegions] = useState(false);

  // Cache initial empty-query page 1 results for instant restore on clear
  const initialRegionTotalPagesRef = useRef(1);

  // Guard against concurrent page loads
  const isLoadingMoreRef = useRef(false);
  // Guard to ensure countries are fetched at most once per mount, so an
  // empty/failed response can't trigger a render → fetch → render loop.
  const hasFetchedCountriesRef = useRef(false);

  useEffect(() => {
    if (countriesOptionsForNumbersSearch?.length === 0) {
      if (!hasFetchedCountriesRef.current) {
        hasFetchedCountriesRef.current = true;
        getCountriesOptionsForNumbersSearchRequest();
      }
      return;
    }
    const initialSelectedCountry = countriesOptionsForNumbersSearch?.find(
      (country) => country.countryIsoCode === 'US',
    );
    const initialSearchFilters: SearchFilters = {
      selectedCountry: {
        isoCode: initialSelectedCountry?.countryIsoCode || '',
        id: initialSelectedCountry?.id || 0,
        isMobileNumberAvailable:
          initialSelectedCountry?.isMobileNumberAvailable || false,
        isLocalNumberAvailable:
          initialSelectedCountry?.isLocalNumberAvailable || false,
        isLocalityRequired:
          initialSelectedCountry?.isLocalityRequired || false,
      },
      // selectedPhoneType: 'local',
      selectedRegion: null,
    };
    setSearchFilters(initialSearchFilters);
    // Make initial api call to get tableData
    searchPhoneNumbersRequest({
      countryCode: initialSelectedCountry?.countryIsoCode || '',
      // numberType: 'local',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countriesOptionsForNumbersSearch]);
  // Note: getCountriesOptionsForNumbersSearchRequest and searchPhoneNumbersRequest
  // are Redux action creators (stable references) and don't need to be in deps

  // Cleanup debounce on unmount
  useEffect(
    () => () => {
      debouncedFetchOptionsRef.current?.cancel();
    },
    [],
  );

  const isSearchButtonDisabled = useMemo(() => {
    if (
      !searchFilters?.selectedCountry
      // || !searchFilters?.selectedPhoneType
    ) {
      return true;
    }
    if (
      // searchFilters?.selectedPhoneType === 'local' && Commenting this as we are not giving user to select phone type
      searchFilters?.selectedCountry?.isLocalityRequired &&
      searchFilters?.selectedRegion === null
    ) {
      return true;
    }
    return false;
  }, [searchFilters]);

  // ----------------------------CONSTRUCTING DROPDOWNS OPTIONS-------------------------------------
  const loadRegionOptions = useCallback(
    async (
      inputValue: string,
      pageNumber: number = 1,
    ): Promise<{ options: AsyncRegionOption[]; totalPages: number }> => {
      try {
        const response = await searchRegions({
          dialerCountryId: searchFilters?.selectedCountry?.id?.toString(),
          searchText: inputValue || '',
          pageNumber,
          pageSize: REGION_PAGE_SIZE,
        });

        const payload = response?.payload;
        const localities = payload?.localities || [];
        const totalPages = payload?.totalPages || 1;

        if (!Array.isArray(localities)) {
          return { options: [], totalPages: 1 };
        }

        const options = localities.map((locality: LocalityApiResponse) => ({
          value: {
            locality: locality?.locality,
            region: locality?.region,
            regionCode: locality?.regionCode,
          },
          label: `${locality.locality}, ${locality.region}`,
          key: locality.id.toString(),
        }));

        return { options, totalPages };
      } catch (error) {
        // Region search failures are handled gracefully by returning empty result
        return { options: [], totalPages: 1 };
      }
    },
    [searchFilters?.selectedCountry],
  );

  const handleRegionFocus = useCallback(async () => {
    setIsRegionFocused(true);
    if (hasLoadedInitialRef.current) {
      // Restore cached page 1 results and reset pagination
      setRegionCurrentPage(1);
      setRegionTotalPages(initialRegionTotalPagesRef.current);
      setRegionOptions(initialRegionOptionsRef.current);
      return;
    }
    setIsRegionLoading(true);
    try {
      const { options, totalPages } = await loadRegionOptions('', 1);
      setRegionOptions(options);
      setRegionCurrentPage(1);
      setRegionTotalPages(totalPages);
      initialRegionOptionsRef.current = options;
      initialRegionTotalPagesRef.current = totalPages;
      hasLoadedInitialRef.current = true;
    } finally {
      setIsRegionLoading(false);
    }
  }, [loadRegionOptions]);

  const handleRegionBlur = useCallback(() => {
    setIsRegionFocused(false);
    debouncedFetchOptionsRef.current?.cancel();
  }, []);

  const handleScrollNearBottom = useCallback(async () => {
    if (isLoadingMoreRef.current || regionCurrentPage >= regionTotalPages) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMoreRegions(true);
    try {
      const nextPage = regionCurrentPage + 1;
      const { options: moreOptions } = await loadRegionOptions(
        regionInputValue,
        nextPage,
      );
      setRegionCurrentPage(nextPage);
      setRegionOptions((prev) => [...prev, ...moreOptions]);
    } finally {
      setIsLoadingMoreRegions(false);
      isLoadingMoreRef.current = false;
    }
  }, [regionCurrentPage, regionTotalPages, regionInputValue, loadRegionOptions]);

  const handleRegionInputChange = useCallback(
    (newValue: string, { action }: { action: string }) => {
      if (action === 'input-blur' || action === 'menu-close') return;
      setRegionInputValue(newValue);
      if (newValue === '') {
        debouncedFetchOptionsRef.current?.cancel();
        setRegionCurrentPage(1);
        setRegionTotalPages(initialRegionTotalPagesRef.current);
        setRegionOptions(initialRegionOptionsRef.current);
        return;
      }
      debouncedFetchOptionsRef.current?.cancel();
      debouncedFetchOptionsRef.current = debounce(async () => {
        setIsRegionLoading(true);
        try {
          const { options, totalPages } = await loadRegionOptions(newValue, 1);
          setRegionCurrentPage(1);
          setRegionTotalPages(totalPages);
          setRegionOptions(options);
        } finally {
          setIsRegionLoading(false);
        }
      }, 500);
      debouncedFetchOptionsRef.current();
    },
    [loadRegionOptions],
  );

  useEffect(() => {
    executeOnRequestStatus({
      status: purchasePhoneNumbersCartRequestState?.status,
      onFailed: () => {
        getPhoneNumbersCartRequest();
        searchPhoneNumbersRequest({
          countryCode: searchFilters?.selectedCountry?.isoCode || '',
        });
        resetPurchasePhoneNumbersCartRequest();
      },
    });
  }, [purchasePhoneNumbersCartRequestState?.status]);

  const PURCHASE_SEARCH_COUNTRY_OPTION = countriesOptionsForNumbersSearch.map(
    (country) => ({
      key: country.countryIsoCode,
      value: {
        isoCode: country.countryIsoCode,
        id: country.id,
      },
      label: `${country.countryName} (${country.countryDialingCode})`,
      isMobileNumberAvailable: country.isMobileNumberAvailable,
      isLocalNumberAvailable: country.isLocalNumberAvailable,
      isLocalityRequired: country?.isLocalityRequired || false,
    }),
  );
  // Compute phone type options with disabled state based on selected country
  const PHONE_TYPE_OPTIONS_WITH_DISABLED = useMemo(
    () =>
      PHONE_TYPE_OPTIONS.map((option) => {
        if (option.key === 'mobile') {
          return {
            ...option,
            isDisabled: searchFilters?.selectedCountry
              ? !searchFilters?.selectedCountry?.isMobileNumberAvailable
              : false,
          };
        }
        if (option.key === 'local') {
          return {
            ...option,
            isDisabled: searchFilters?.selectedCountry
              ? !searchFilters?.selectedCountry?.isLocalNumberAvailable
              : false,
          };
        }
        return option;
      }),
    [searchFilters?.selectedCountry],
  );
  const PHONE_TYPE_OPTIONS_WITH_DISABLED_REMOVED = useMemo(
    () =>
      PHONE_TYPE_OPTIONS_WITH_DISABLED.filter(
        (option: PhoneTypeOption) => option?.isDisabled === false,
      ),
    [searchFilters?.selectedCountry],
  );
  // ----------------------------CONSTRUCTING DROPDOWN OPTIONS-------------------------------------
  const handleNumbersSearch = useCallback(() => {
    if (
      !searchFilters?.selectedCountry
      // || !searchFilters?.selectedPhoneType
    ) {
      return;
    }

    const payload: SearchPhoneNumbersPayload = {
      countryCode: searchFilters?.selectedCountry?.isoCode || '',
      // numberType: searchFilters?.selectedPhoneType || '',
    };

    const currentRegion = searchFilters?.selectedRegion?.region || null;

    // Check if region changed compared to previous search
    const regionChanged = previousRegionRef.current !== currentRegion;

    // Update Redux state
    setIsRegionChangedInLastSearch(regionChanged);
    setLastSearchRegion(currentRegion);

    // Update the ref for next comparison
    previousRegionRef.current = currentRegion;

    if (searchFilters?.selectedRegion) {
      payload.region = searchFilters?.selectedRegion?.regionCode || '';
      payload.locality = searchFilters?.selectedRegion?.locality || '';
    }

    searchPhoneNumbersRequest(payload);
  }, [
    searchFilters,
    searchPhoneNumbersRequest,
    setIsRegionChangedInLastSearch,
    setLastSearchRegion,
  ]);
  return (
    <div className="select-numbers-step-search-inputs-wrapper">
      <Select<{
        key: string;
        value: { isoCode: string; id: number };
        label: string;
        isMobileNumberAvailable: boolean;
        isLocalNumberAvailable: boolean;
        isLocalityRequired: boolean;
      }>
        className="select-numbers-step-dropdown"
        options={PURCHASE_SEARCH_COUNTRY_OPTION}
        selectedOptionKey={searchFilters?.selectedCountry?.isoCode || ''}
        optionRenderer={(option) => (
          <div className="d-flex align-items-center">
            <ImageIcon
              src={COUNTRY_ISO_FLAG_MAP[option.value.isoCode]}
              alt={`${option.label} flag`}
              width={18}
              height={14}
            />
            <span className="ml-2">{option.label}</span>
          </div>
        )}
        onChange={([option]) => {
          setSearchFilters((prev) => ({
            ...prev,
            selectedCountry: {
              ...option?.value,
              isMobileNumberAvailable: option?.isMobileNumberAvailable,
              isLocalNumberAvailable: option?.isLocalNumberAvailable,
              isLocalityRequired: option?.isLocalityRequired,
            },
            selectedPhoneType: PHONE_TYPE_OPTIONS_WITH_DISABLED_REMOVED[0]?.key, // Clear phone type on country change
            selectedRegion: null, // Reset region when country changes
          }));
          setSelectedRegionOption(null); // Reset region selection
          setRegionInputValue(''); // Reset input value
          previousRegionRef.current = null; // Reset region tracking when country changes
          // Reset initial options cache so next focus re-fetches for new country
          hasLoadedInitialRef.current = false;
          initialRegionOptionsRef.current = [];
          initialRegionTotalPagesRef.current = 1;
          isLoadingMoreRef.current = false;
          setRegionCurrentPage(1);
          setRegionTotalPages(1);
          setIsLoadingMoreRegions(false);
          setRegionOptions([]);
        }}
        selectedOptionRenderer={([option]) => (
          <div className="d-flex align-items-center">
            <ImageIcon
              src={COUNTRY_ISO_FLAG_MAP[option?.value?.isoCode]}
              alt={`${option?.label} flag`}
              width={18}
              height={14}
            />
            <span className="ml-2">{option?.label}</span>
          </div>
        )}
        placeholder="Select Country"
        searchPlaceholder="Search country"
        disabled={isSearching}
      />
      {/* <Select<PhoneTypeOption>
        className="select-numbers-step-dropdown"
        options={PHONE_TYPE_OPTIONS_WITH_DISABLED_REMOVED}
        selectedOptionKey={searchFilters?.selectedPhoneType || ''}
        optionRenderer={(option) => (
          <span
            className={option.isDisabled ? 'phone-type-option-disabled' : ''}
          >
            {option.value}
          </span>
        )}
        onChange={([option]) => {
          // Prevent selection if option is disabled
          if (option?.isDisabled) {
            return;
          }
          setSearchFilters((prev) => ({
            ...prev,
            selectedPhoneType: option?.key || null,
          }));
        }}
        selectedOptionRenderer={([option]) => <span>{option?.value}</span>}
        placeholder="Select Phone Type"
        disabled={isSearching}
      /> */}
      <div className="select-numbers-step-dropdown">
        <ReactSelect<AsyncRegionOption>
          className="react-select-container"
          classNamePrefix="react-select"
          options={regionOptions}
          isLoading={isRegionLoading}
          value={selectedRegionOption}
          inputValue={regionInputValue}
          onInputChange={handleRegionInputChange}
          components={{ MenuList: RegionMenuList }}
          onScrollNearBottom={handleScrollNearBottom}
          isLoadingMoreRegions={isLoadingMoreRegions}
          onChange={(option) => {
            if (option === null) {
              setSelectedRegionOption(null);
              setRegionInputValue('');
              setRegionCurrentPage(1);
              setRegionTotalPages(initialRegionTotalPagesRef.current);
              setRegionOptions(initialRegionOptionsRef.current);
              setSearchFilters((prev) => ({ ...prev, selectedRegion: null }));
              return;
            }
            setSelectedRegionOption(option as AsyncRegionOption);
            setRegionInputValue('');
            setIsRegionFocused(false);
            setSearchFilters((prev) => ({
              ...prev,
              selectedRegion: {
                ...option.value,
              } as AsyncRegionOption['value'],
            }));
          }}
          onFocus={handleRegionFocus}
          onBlur={handleRegionBlur}
          menuIsOpen={isRegionFocused || regionInputValue.length > 0}
          filterOption={() => true}
          placeholder="Local Area or State"
          styles={customStyles}
          isClearable
          noOptionsMessage={({ inputValue }) =>
            inputValue ? 'No regions found' : null
          }
          loadingMessage={() => 'Loading regions...'}
          isDisabled={isSearching}
        />
      </div>
      <Button
        isLoading={isSearching}
        disabled={isSearchButtonDisabled || isSearching}
        className="select-numbers-step-search-button"
        loadingText="Searching..."
        {...accessibleOnClick(handleNumbersSearch)}
      >
        Search
      </Button>
    </div>
  );
};

export default SearchNumbers;
